// Dealzta authentication service.
//
// Dealzta is the source of truth for user accounts. SelfieBooth's Login
// screen authenticates directly against Dealzta's existing login endpoint
// instead of a second/local user system. Uses the browser's native fetch,
// matching the other service files in this folder (no new dependency).

const DEALZTA_BASE_URL = (process.env.REACT_APP_DEALZTA_BASE_URL || 'https://api.dealzta.com').replace(/\/+$/, '');

const STORAGE_KEYS = {
  accessToken: 'dealzta_access_token',
  refreshToken: 'dealzta_refresh_token',
  sessionId: 'dealzta_session_id',
  user: 'dealzta_user',
};

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    // Storage unavailable (private browsing, quota, etc.) — auth still
    // works for the current in-memory session, it just won't persist.
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    // Ignore — nothing to clean up if storage isn't available.
  }
}

/**
 * Authenticate against Dealzta's login endpoint (POST /api/v1/auth/login).
 * The response nests its auth fields under `data`
 * ({ success, message, data: { access_token, user, ... } }).
 *
 * On success, stores access_token/refresh_token/session_id/user in
 * localStorage and resolves to { user, accessToken, refreshToken,
 * sessionId, tokenType, expiresIn }.
 *
 * Throws an Error with a user-friendly message on invalid credentials, an
 * inactive account, a network failure, or a malformed response (missing
 * access_token/user). Never logs the password, access_token or
 * refresh_token.
 */
// export async function login(identifier, password) {
//   let response;
//   try {
//     response = await fetch(`${DEALZTA_BASE_URL}/api/v1/auth/login`, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//         Accept: 'application/json',
//       },
//       body: JSON.stringify({ identifier, password }),
//     });
//   } catch (err) {
//     throw new Error('Unable to reach the server. Check your connection and try again.');
//   }

//   let body = null;
//   try {
//     body = await response.json();
//   } catch (err) {
//     body = null;
//   }

//   if (!response.ok) {
//     const serverMessage = body && (body.message || body.error);
//     if (serverMessage) {
//       throw new Error(serverMessage);
//     }
//     if (response.status === 401 || response.status === 422) {
//       throw new Error('Incorrect username/email/phone or password.');
//     }
//     if (response.status === 403) {
//       throw new Error('This account is inactive. Please contact support.');
//     }
//     throw new Error('Login failed. Please try again.');
//   }

//   const data = body && body.data;
//   const accessToken = data && data.access_token;
//   const user = data && data.user;

//   if (!accessToken || !user) {
//     throw new Error('Unexpected response from the server. Please try again.');
//   }

//   writeStorage(STORAGE_KEYS.accessToken, accessToken);
//   if (data.refresh_token) writeStorage(STORAGE_KEYS.refreshToken, data.refresh_token);
//   if (data.session_id) writeStorage(STORAGE_KEYS.sessionId, data.session_id);
//   writeStorage(STORAGE_KEYS.user, JSON.stringify(user));

//   return {
//     user,
//     accessToken,
//     refreshToken: data.refresh_token ?? null,
//     sessionId: data.session_id ?? null,
//     tokenType: data.token_type ?? null,
//     expiresIn: data.expires_in ?? null,
//   };
// }

export async function login(identifier, password) {
  let response;

  try {
    response = await fetch(`${DEALZTA_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      credentials: 'include', // IMPORTANT: accept/send cookies
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Client-Type': 'web',
      },
      body: JSON.stringify({
        identifier,
        password,
      }),
    });
  } catch (err) {
    throw new Error(
      'Unable to reach the server. Check your connection and try again.'
    );
  }

  let body = null;

  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }

  if (!response.ok) {
    const serverMessage = body && (body.message || body.error);

    if (serverMessage) {
      throw new Error(serverMessage);
    }

    if (response.status === 401 || response.status === 422) {
      throw new Error(
        'Incorrect username/email/phone or password.'
      );
    }

    if (response.status === 403) {
      throw new Error(
        'This account is inactive. Please contact support.'
      );
    }

    throw new Error('Login failed. Please try again.');
  }

  const data = body && body.data;
  const user = data && data.user;

  // Website only needs the user.
  // Tokens are stored in HttpOnly cookies by Laravel.
  if (!user) {
    throw new Error(
      'Unexpected response from the server. Please try again.'
    );
  }

  // Store only non-sensitive user information if you need it.
  writeStorage(
    STORAGE_KEYS.user,
    JSON.stringify(user)
  );

  return {
    user,
  };
}

/**
 * Clears every stored Dealzta auth value. Local-only by design: it never
 * calls Dealzta's /auth/logout, so logging out of SelfieBooth does not end
 * the user's Dealzta session. An in-flight refreshAccessToken() cannot write
 * a token back afterwards (doRefresh() re-checks the stored refresh token).
 */
export function logout() {
  removeStorage(STORAGE_KEYS.accessToken);
  removeStorage(STORAGE_KEYS.refreshToken);
  removeStorage(STORAGE_KEYS.sessionId);
  removeStorage(STORAGE_KEYS.user);
}

// Thrown by refreshAccessToken() when Dealzta definitively rejects the
// stored refresh token/session (or none is stored). Stored auth has already
// been cleared by the time this is thrown — only a real login can recover.
export const SESSION_EXPIRED = 'SESSION_EXPIRED';

// The single in-flight refresh, shared by every caller that asks for one
// while it's still pending, so concurrent 401s never fire more than one
// POST /auth/refresh.
let refreshPromise = null;

/**
 * Exchanges the stored refresh_token (+ session_id) for a new access token
 * via Dealzta's existing POST /api/v1/auth/refresh — the same Dealzta
 * session is reused, never a new login. On success only
 * dealzta_access_token is replaced (refresh_token, session_id and user are
 * kept as-is) and the new access token is resolved.
 *
 * On a definitive rejection (no refresh token stored, or Dealzta answers
 * 4xx: invalid/expired refresh token or session) stored auth is cleared and
 * an Error with code SESSION_EXPIRED is thrown. A network failure or 5xx
 * throws without clearing anything — a temporarily unreachable Dealzta is
 * not an expired session. Never logs any token or the session id.
 */
export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function doRefresh() {
  const refreshToken = readStorage(STORAGE_KEYS.refreshToken);
  const sessionId = readStorage(STORAGE_KEYS.sessionId);

  if (!refreshToken) {
    logout();
    throw Object.assign(new Error('Your session has expired. Please log in again.'), { code: SESSION_EXPIRED });
  }

  let response;
  try {
    response = await fetch(`${DEALZTA_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken, session_id: sessionId }),
    });
  } catch (err) {
    throw new Error('Unable to reach the server. Check your connection and try again.');
  }

  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }

  // A logout (or a fresh login) that happened while this request was in
  // flight must never be overwritten or wiped by its late result: use
  // whatever session is stored now instead.
  if (readStorage(STORAGE_KEYS.refreshToken) !== refreshToken) {
    const currentToken = readStorage(STORAGE_KEYS.accessToken);
    if (currentToken) return currentToken;
    throw Object.assign(new Error('Your session has expired. Please log in again.'), { code: SESSION_EXPIRED });
  }

  const accessToken = response.ok && body && body.data && body.data.access_token;

  if (!accessToken) {
    if (response.status >= 400 && response.status < 500) {
      logout();
      throw Object.assign(new Error('Your session has expired. Please log in again.'), { code: SESSION_EXPIRED });
    }
    throw new Error('Unable to refresh your session. Please try again.');
  }

  writeStorage(STORAGE_KEYS.accessToken, accessToken);
  return accessToken;
}

export function getStoredToken() {
  return readStorage(STORAGE_KEYS.accessToken);
}

export function getStoredUser() {
  const raw = readStorage(STORAGE_KEYS.user);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

/**
 * True only when both a stored access token and a stored user are present.
 */
export function isAuthenticated() {
  return Boolean(getStoredToken() && getStoredUser());
}
